import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  LockKeyhole,
  FileCheck2,
  Database,
  UserCheck,
  Eye,
  Server,
  Check,
  ArrowRight,
  Scale,
  HeartPulse,
  ClipboardCheck,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const Compliance = () => {
  const principles = [
    {
      icon: ShieldCheck,
      title: "Security by Design",
      description:
        "Security should be considered throughout the design and development of digital healthcare experiences.",
    },
    {
      icon: LockKeyhole,
      title: "Data Protection",
      description:
        "Protecting information is an important part of building trustworthy healthcare technology.",
    },
    {
      icon: UserCheck,
      title: "Responsible Access",
      description:
        "Access to healthcare information should be handled responsibly and according to the appropriate requirements.",
    },
    {
      icon: Eye,
      title: "Transparency",
      description:
        "Clear policies and responsible practices help users understand how their information is handled.",
    },
  ];

  const areas = [
    {
      icon: Database,
      title: "Data Governance",
      description:
        "Establishing appropriate processes for managing healthcare and user information throughout its lifecycle.",
    },
    {
      icon: Server,
      title: "Infrastructure Security",
      description:
        "Supporting secure and reliable infrastructure for digital healthcare services.",
    },
    {
      icon: ClipboardCheck,
      title: "Operational Controls",
      description:
        "Using appropriate processes and controls to support consistent and responsible operations.",
    },
    {
      icon: FileCheck2,
      title: "Policy & Documentation",
      description:
        "Maintaining clear policies and documentation around privacy, security and responsible technology.",
    },
  ];

  const commitments = [
    "Protect information through appropriate security practices",
    "Apply responsible access and data-handling principles",
    "Continuously review security and operational practices",
    "Support transparency around data and privacy",
    "Consider applicable healthcare and privacy requirements",
    "Build trust into digital healthcare experiences",
  ];

  const complianceJourney = [
    {
      number: "01",
      title: "Understand",
      description:
        "Identify the information, workflows and risks relevant to each healthcare experience.",
    },
    {
      number: "02",
      title: "Protect",
      description:
        "Apply appropriate security, privacy and access controls.",
    },
    {
      number: "03",
      title: "Review",
      description:
        "Regularly evaluate processes and identify opportunities for improvement.",
    },
    {
      number: "04",
      title: "Improve",
      description:
        "Continue strengthening practices as technology, requirements and risks evolve.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center">

            {/* HERO CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Doxzon Compliance
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Trust is built
                <span className="block text-teal-500 mt-2">
                  into everything we do.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Doxzon is committed to responsible healthcare technology,
                with privacy, security and appropriate data practices at the
                centre of the digital healthcare experience.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-slate-800 transition">

                  Our Security Approach

                  <ArrowRight size={16} />

                </button>

                <button className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 transition">

                  Privacy & Security

                </button>

              </div>

            </motion.div>

            {/* HERO VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative rounded-[32px] bg-white border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-7">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Trust Framework
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Security & Privacy
                    </h3>

                  </div>

                  <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                    <ShieldCheck
                      size={24}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* SECURITY VISUAL */}
                <div className="relative flex justify-center my-10">

                  <div className="absolute w-56 h-56 rounded-full border border-teal-100" />

                  <div className="absolute w-40 h-40 rounded-full border border-teal-100" />

                  <div className="relative z-10 w-24 h-24 rounded-[28px] bg-slate-950 grid place-items-center shadow-xl">

                    <LockKeyhole
                      size={38}
                      className="text-teal-400"
                    />

                  </div>

                </div>

                <div className="grid grid-cols-2 gap-3">

                  {[
                    [Database, "Data Protection"],
                    [UserCheck, "Access Control"],
                    [Server, "Infrastructure"],
                    [FileCheck2, "Documentation"],
                  ].map(([Icon, title]) => (

                    <div
                      key={title}
                      className="rounded-2xl bg-slate-50 border border-slate-100 p-4"
                    >

                      <div className="w-9 h-9 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={17}
                          className="text-teal-600"
                        />

                      </div>

                      <p className="text-xs font-semibold mt-3">
                        {title}
                      </p>

                    </div>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            COMPLIANCE PRINCIPLES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Our Principles
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Compliance starts with
                <span className="block text-teal-500">
                  responsible design.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Responsible healthcare technology requires more than a single
                security feature. It requires thoughtful processes across
                products, people and operations.
              </p>

            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {principles.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="rounded-3xl border border-slate-100 bg-slate-50/70 p-7 hover:bg-white hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={22}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            SECURITY AREAS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Key Areas
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                A layered approach to
                <span className="text-teal-500">
                  {" "}trust.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Our approach considers the different layers involved in
                building and operating digital healthcare services.
              </p>

            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-12">

              {areas.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -5 }}
                    className="group rounded-3xl bg-white border border-slate-100 p-7 md:p-8 hover:border-teal-100 hover:shadow-xl transition"
                  >

                    <div className="flex gap-5">

                      <div className="w-14 h-14 rounded-2xl bg-teal-50 grid place-items-center shrink-0 group-hover:bg-teal-500 transition">

                        <Icon
                          size={24}
                          className="text-teal-600 group-hover:text-white transition"
                        />

                      </div>

                      <div>

                        <h3 className="font-sora text-xl font-semibold">
                          {item.title}
                        </h3>

                        <p className="text-slate-500 text-sm leading-7 mt-3">
                          {item.description}
                        </p>

                      </div>

                    </div>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            COMPLIANCE JOURNEY
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Continuous Process
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Compliance isn't a
                <span className="block text-teal-400">
                  one-time task.
                </span>

              </h2>

              <p className="text-slate-400 leading-7 mt-5">
                Responsible security and compliance require continuous
                attention as technology, risks and requirements evolve.
              </p>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-white/10" />

              {complianceJourney.map((step) => (

                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="relative text-center"
                >

                  <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-teal-500 border-4 border-slate-950 shadow-lg grid place-items-center">

                    <span className="text-white text-xs font-bold">
                      {step.number}
                    </span>

                  </div>

                  <h3 className="font-sora text-lg font-semibold mt-6">
                    {step.title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                    {step.description}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            COMMITMENTS
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Our Commitment
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Trust is something
                <span className="block text-teal-500">
                  we continuously earn.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Healthcare technology carries a high level of responsibility.
                We aim to build practices that support privacy, security and
                trustworthy digital experiences.
              </p>

              <div className="flex items-center gap-3 mt-7">

                <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                  <HeartPulse
                    size={19}
                    className="text-teal-600"
                  />

                </div>

                <span className="text-sm font-semibold text-slate-700">
                  Healthcare remains at the centre of our approach.
                </span>

              </div>

            </div>

            <div className="space-y-3">

              {commitments.map((item) => (

                <motion.div
                  key={item}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4"
                >

                  <span className="w-8 h-8 rounded-full bg-white text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            DISCLAIMER / DOCUMENTS
        ====================================================== */}
        <section className="py-20 bg-[#f6fbfc]">

          <div className="max-w-5xl mx-auto px-6">

            <div className="rounded-3xl bg-white border border-slate-100 p-7 md:p-10">

              <div className="flex gap-5">

                <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center shrink-0">

                  <Scale
                    size={22}
                    className="text-teal-600"
                  />

                </div>

                <div>

                  <h2 className="font-sora text-2xl font-semibold">
                    Regulatory & Compliance Information
                  </h2>

                  <p className="text-slate-500 text-sm leading-7 mt-4">
                    Healthcare and privacy requirements can vary depending on
                    the country, region, service and type of information
                    involved. Doxzon's specific regulatory obligations,
                    certifications and compliance status should be documented
                    according to the actual services and jurisdictions in
                    which the platform operates.
                  </p>

                  <p className="text-slate-500 text-sm leading-7 mt-3">
                    Please refer to the applicable Doxzon policies and legal
                    documentation for authoritative information about specific
                    privacy, security and regulatory requirements.
                  </p>

                  <div className="flex flex-wrap gap-3 mt-6">

                    <button className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 h-11 text-sm font-semibold hover:border-teal-200 hover:text-teal-600 transition">

                      Privacy Policy

                      <ArrowRight size={15} />

                    </button>

                    <button className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 h-11 text-sm font-semibold hover:border-teal-200 hover:text-teal-600 transition">

                      Security Information

                      <ArrowRight size={15} />

                    </button>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            CTA
        ====================================================== */}
        <section className="px-6 py-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <div className="mx-auto w-12 h-12 rounded-2xl bg-white/10 grid place-items-center">

                <ShieldCheck
                  size={22}
                  className="text-teal-400"
                />

              </div>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-6">

                Trust matters in
                <span className="text-teal-400">
                  {" "}healthcare.
                </span>

              </h2>

              <p className="text-slate-400 max-w-2xl mx-auto mt-5 leading-7">
                Learn more about Doxzon's approach to privacy, security and
                responsible healthcare technology.
              </p>

              <button className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition">

                Explore Security & Privacy

                <ArrowRight size={17} />

              </button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Compliance;